import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("lpg_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const WAKE_STATUSES = [502, 503, 504];
const MAX_RETRIES = 6;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const endWaking = (cfg) => {
  if (!cfg?.__retry || cfg.__wakeDone) return;
  cfg.__wakeDone = true;
  window.dispatchEvent(new CustomEvent("server-waking", { detail: -1 }));
};

// Retry reads while the server is waking up.
api.interceptors.response.use(undefined, async (err) => {
  const cfg = err.config;
  if (!cfg) throw err;
  const status = err.response?.status;
  const isRead = (cfg.method || "get").toLowerCase() === "get";
  const noResponse = !err.response;
  // Writes are never auto-retried so an entry can't be saved twice.
  const retryable = isRead && (noResponse || WAKE_STATUSES.includes(status));
  cfg.__retry = cfg.__retry || 0;
  if (retryable && cfg.__retry < MAX_RETRIES) {
    if (cfg.__retry === 0) window.dispatchEvent(new CustomEvent("server-waking", { detail: 1 }));
    cfg.__retry += 1;
    await sleep(1500 * cfg.__retry);
    return api(cfg);
  }
  endWaking(cfg);
  if (noResponse || WAKE_STATUSES.includes(status)) window.dispatchEvent(new Event("server-asleep"));
  if (status === 401 && !/\/auth\/(login|me)/.test(cfg.url || "")) {
    localStorage.removeItem("lpg_token");
    window.dispatchEvent(new Event("session-expired"));
  }
  if (noResponse) {
    err.response = { data: { detail: "Server is not reachable. Check internet, then verify the entry before saving again." } };
  } else if (WAKE_STATUSES.includes(status)) {
    err.response.data = { detail: "Server is waking up. Please try again in a few seconds." };
  }
  throw err;
});

api.interceptors.response.use((resp) => {
  endWaking(resp.config);
  return resp;
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatMoney(n) {
  const num = Number(n || 0);
  return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = typeof iso === "string" && iso.length <= 10 ? new Date(iso + "T00:00:00") : new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

export function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
